// Stock lines and their filters: the warehouse's Stok screen, and a seller's "Ürünler ve stok",
// where each catalog row carries its product's stock line.
import type { ProductListItem, StockLine } from '@wms/domain';

/** The seller's filters, in the order the chips show them. */
export const STOCK_FILTERS = [
  { key: 'all', label: 'Tümü' },
  { key: 'in_stock', label: 'Stokta' },
  { key: 'out', label: 'Tükendi' },
  { key: 'soon', label: "SKT'si yaklaşan" },
  { key: 'expired', label: 'Süresi geçen' },
  { key: 'tracked', label: 'Lot / SKT takipli' },
] as const;
export type StockFilter = (typeof STOCK_FILTERS)[number]['key'];

type Stocked = Pick<StockLine, 'available' | 'expiringQty' | 'expiredQty' | 'trackLot' | 'trackExpiry'>;

/** Whether a line is in a filter; "SKT'si yaklaşan" counts the expired ones too, as the start screens do. */
export function inFilter(line: Stocked, filter: StockFilter): boolean {
  switch (filter) {
    case 'in_stock':
      return line.available > 0;
    case 'out':
      return line.available <= 0;
    case 'soon':
      return line.expiringQty > 0 || line.expiredQty > 0;
    case 'expired':
      return line.expiredQty > 0;
    case 'tracked':
      return line.trackLot || line.trackExpiry;
    default:
      return true;
  }
}

/** The filter a link opens on: #/urunler?skt=yakin (old #/stok links keep theirs), ?stok=tukendi … */
export function linkedFilter(query: URLSearchParams): StockFilter | null {
  const skt = query.get('skt');
  if (skt === 'yakin') return 'soon';
  if (skt === 'gecti') return 'expired';
  const stock = query.get('stok');
  if (stock === 'var') return 'in_stock';
  if (stock === 'tukendi') return 'out';
  return null;
}

/** A catalog row with its stock line. */
export interface ProductStock extends ProductListItem {
  readonly reserved: number;
  readonly inbound: number;
  readonly blocked: number;
  readonly minShelfLifeDays: number | null;
  readonly nextExpiry: string | null;
  readonly expiringQty: number;
  readonly expiredQty: number;
}

/**
 * The catalog rows with their stock lines (both lists come seller-scoped from the server). A product
 * added since the stock list was loaded has no line yet: it keeps what the catalog says, and no lots.
 */
export function withStock(products: readonly ProductListItem[], lines: readonly StockLine[]): ProductStock[] {
  const byProduct = new Map(lines.map((l) => [l.productId as string, l]));
  return products.map((p) => {
    const line = byProduct.get(p.id);
    if (!line) return { ...p, reserved: 0, inbound: 0, blocked: 0, minShelfLifeDays: null, nextExpiry: null, expiringQty: 0, expiredQty: 0 };
    return {
      ...p,
      physical: line.physical,
      available: line.available,
      reserved: line.reserved,
      inbound: line.inbound,
      blocked: line.blocked,
      minShelfLifeDays: line.minShelfLifeDays,
      nextExpiry: line.nextExpiry,
      expiringQty: line.expiringQty,
      expiredQty: line.expiredQty,
    };
  });
}
